/**
 * The clinician/practice trial-ended modal's analytics vocabulary: the `action`
 * tokens its six CTAs carry, the payload shapes its two events carry, and the
 * emit seams the app binds.
 *
 * ONE SURFACE, TWO SCREENS. The modal reports as `clinician_trial_ended_modal`
 * or `practice_trial_ended_modal` depending on which plan the reverse trial
 * displaced — one component, one cohort decision, two warehouse rows. The
 * cohort is computed on the domain side (`getReverseTrialCohort`) and travels in
 * `screen_name`, so the host never re-derives it.
 *
 * The `action` tokens keep the `clinician_trial_ended_` prefix on both screens,
 * including the practice branch. That reads wrong and is correct: the tokens are
 * warehouse keys that predate the practice branch, and re-spelling one to match
 * its screen would split the metric it already feeds.
 */
import type { SubscriptionTier } from '@heidi/schema/ml-scribe';

import type {
  TRIALS_AND_UPGRADES_SCREEN,
  TrialPlanContext,
} from './trials-and-upgrades-analytics.types';

/** The two screens this one modal reports as. */
export type ClinicianTrialEndedScreenToken =
  | (typeof TRIALS_AND_UPGRADES_SCREEN)['clinicianTrialEnded']
  | (typeof TRIALS_AND_UPGRADES_SCREEN)['practiceTrialEnded'];

/** The `action` tokens the modal's six CTAs carry. */
export const CLINICIAN_TRIAL_ENDED_ACTION = {
  keepClinician: 'clinician_trial_ended_keep_clinician',
  keepPractice: 'clinician_trial_ended_keep_practice',
  requestToUpgrade: 'clinician_trial_ended_request_to_upgrade',
  remain: 'clinician_trial_ended_remain',
  talkToUs: 'clinician_trial_ended_talk_to_us',
  contactSales: 'clinician_trial_ended_contact_sales',
} as const;

export type ClinicianTrialEndedActionToken =
  (typeof CLINICIAN_TRIAL_ENDED_ACTION)[keyof typeof CLINICIAN_TRIAL_ENDED_ACTION];

/** `modal_viewed` — the modal became visible. Fired once per open. */
export type ClinicianTrialEndedViewedProps = {
  screen_name: ClinicianTrialEndedScreenToken;
};

/**
 * `button_clicked` on any of the modal's six CTAs.
 *
 * One payload type and one seam rather than six, because the six differ only in
 * their `action` token and the two upgrade CTAs' extra plan fields — which is
 * how the surface already emits them. Splitting them would put six near-identical
 * seams on the domain hook for no assertion the `action` union does not already
 * make.
 *
 * `label` is DOMAIN-COMPUTED and travels in the payload: every one of the six is
 * `intl.formatMessage(...)` over a descriptor this surface owns, so a host that
 * re-derived it would be translating copy it does not hold.
 *
 * `target_plan` and `period` are present only on the two self-serve upgrade CTAs
 * — the tier the click is buying and the Stripe cadence it matches. `period` is
 * a plain string rather than the app's `Period` enum: that enum lives in the
 * app's type tree, and the value reaching this payload can also be a
 * `plan_cycle` read straight off billing info.
 */
export type ClinicianTrialEndedClickProps = TrialPlanContext & {
  screen_name: ClinicianTrialEndedScreenToken;
  action: ClinicianTrialEndedActionToken;
  label: string;
  target_plan?: SubscriptionTier;
  period?: string;
};

/**
 * The emit seam for the modal becoming visible.
 *
 * The open-state guard stays on the domain side: it is a payload decision (this
 * open has not been reported yet), not a transport one.
 */
export type TrackClinicianTrialEndedViewed = (
  props: ClinicianTrialEndedViewedProps
) => void;

/** The emit seam for any of the six CTAs. */
export type TrackClinicianTrialEndedClick = (
  props: ClinicianTrialEndedClickProps
) => void;

/**
 * The two seams as one app-supplied bundle.
 *
 * Held as two independently stable functions rather than read off this object:
 * `trackViewed` is a dependency of the open effect, so a port whose identity
 * changes per render would re-fire it and double-count the view.
 */
export type ClinicianTrialEndedAnalyticsPorts = {
  trackViewed: TrackClinicianTrialEndedViewed;
  trackClick: TrackClinicianTrialEndedClick;
};
